import { useState } from "react";
import { useParams, Link, useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import * as LucideIcons from "lucide-react";
import {
  ArrowRight, Check, X, Lock, CalendarCheck, Sparkles, Layers, Plus,
} from "lucide-react";
import { moduleById, baseById, BASES, ADDONS } from "@/config/clinicOS";
import { MODULE_CONTENT } from "@/config/moduleContent";
import { IncludedEverywhere } from "@/components/landing/IncludedEverywhere";
import { StartPlanModal } from "@/components/landing/StartPlanModal";
import { useDemo } from "@/components/landing/DemoContext";
import {
  PainPoints, DEFAULT_STATS, GROWTH_STATS, COMMS_STATS, DATA_STATS,
} from "@/components/welcome/PainPoints";
import { moduleDiscFor } from "@/data/moduleDisc";

const STAT_SETS = {
  DEFAULT: DEFAULT_STATS,
  GROWTH:  GROWTH_STATS,
  COMMS:   COMMS_STATS,
  DATA:    DATA_STATS,
};

const PAINPOINT_HEADINGS = {
  D: { eyebrow: "The growth math", heading: "The four numbers a module like this should move." },
  i: { eyebrow: "The conversation gap", heading: "The four numbers a module like this should close." },
  S: { eyebrow: "The calm math", heading: "The four numbers a module like this should steady." },
  C: { eyebrow: "The data math", heading: "The four numbers a module like this should surface." },
};

export default function ModulePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const m = moduleById(slug);
  const content = MODULE_CONTENT[slug];
  const openDemo = useDemo();

  const [modalBase, setModalBase] = useState(null);

  if (!m || !content) return <Navigate to="/" replace />;

  const Icon = m.icon;
  const isBase = !!BASES.find((b) => b.id === m.id);
  const reqBase = m.requiresBase ? baseById(m.requiresBase) : null;

  // Open StartPlanModal with the right base pre-selected.
  // For bases → the base itself. For add-ons → the required base (or Hub+ default).
  const openStart = (baseId) => setModalBase(baseId);

  // Related modules: for a base → top 3 add-ons; for an add-on → 3 other add-ons.
  const related = isBase
    ? ADDONS.slice(0, 3)
    : ADDONS.filter((a) => a.id !== m.id).slice(0, 3);

  return (
    <article data-testid={`module-page-${m.id}`} className="overflow-hidden">
      {/* HERO */}
      <section className="relative pt-16 pb-10 sm:pt-40 sm:pb-24">
        <div className={`pointer-events-none absolute inset-x-0 top-0 -z-10 h-[520px] bg-gradient-to-b ${m.color} opacity-10 blur-3xl`} />
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
          <div className="grid items-start gap-8 sm:gap-12 lg:grid-cols-[1.25fr_1fr] lg:gap-14">
            <div>
              <div className="hidden flex-wrap items-center gap-2 sm:flex">
                <span className={`inline-flex items-center gap-2 rounded-full ${m.bg} ${m.accent} px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.16em]`}>
                  <Icon className="h-3.5 w-3.5" />
                  {isBase ? "Base · required" : "Snap-on add-on"}
                </span>
                {isBase && m.badge && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-white">
                    <Sparkles className="h-3 w-3" />
                    {m.badge}
                  </span>
                )}
                {!isBase && reqBase && (
                  <Link
                    to={`/products/${reqBase.id}`}
                    data-testid="module-requires-link"
                    className="inline-flex items-center gap-1.5 rounded-full bg-violet-500/15 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-violet-500 transition-colors hover:bg-violet-500/25"
                  >
                    <Lock className="h-3 w-3" />
                    Requires {reqBase.name}
                  </Link>
                )}
                {!isBase && !reqBase && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-500">
                    Works with Hub or Hub+
                  </span>
                )}
              </div>

              {/* Mobile: single small chip showing only the most important badge */}
              <div className="flex flex-wrap items-center gap-2 sm:hidden">
                <span className={`inline-flex items-center gap-1.5 rounded-full ${m.bg} ${m.accent} px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em]`}>
                  <Icon className="h-3 w-3" />
                  {isBase ? "Base" : "Add-on"}
                </span>
                {!isBase && reqBase && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-violet-500">
                    <Lock className="h-2.5 w-2.5" />
                    Requires {reqBase.short || reqBase.name}
                  </span>
                )}
              </div>

              <h1 className="mt-4 sm:mt-6 font-display text-[2.4rem] font-black leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl">
                {m.name.split(" ").map((word, i, arr) => (
                  i === arr.length - 1 ? (
                    <span key={i} className={`bg-gradient-to-r ${m.color} bg-clip-text text-transparent`}>{word}</span>
                  ) : (
                    <span key={i}>{word}{i < arr.length - 1 ? " " : ""}</span>
                  )
                ))}
              </h1>
              <p className="mt-3 sm:mt-5 max-w-2xl font-display text-[15px] font-bold leading-snug tracking-tight text-foreground/90 sm:text-2xl">
                {content.longTagline}
              </p>
              <p className="mt-2.5 sm:mt-4 max-w-2xl text-[14px] leading-relaxed text-muted-foreground sm:text-base">
                {content.heroBody}
              </p>

              <div className="mt-5 sm:mt-8 flex flex-wrap items-center gap-2 sm:gap-3">
                {isBase ? (
                  <>
                    <button
                      type="button"
                      onClick={() => openStart(m.id)}
                      data-testid="module-start-cta"
                      className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-violet-600/25 transition-all hover:-translate-y-0.5"
                    >
                      <CalendarCheck className="h-4 w-4" />
                      Start with {m.short}
                      <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                    <Link
                      to="/build"
                      data-testid="module-build-cta"
                      className="hidden sm:inline-flex items-center gap-2 rounded-xl border border-border bg-card px-5 py-3.5 text-sm font-semibold transition-all hover:-translate-y-0.5 hover:bg-secondary"
                    >
                      <Layers className="h-4 w-4" />
                      Compare plans
                    </Link>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => openStart(reqBase ? reqBase.id : "hub-plus")}
                      data-testid="module-start-cta"
                      className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-violet-600/25 transition-all hover:-translate-y-0.5"
                    >
                      <Plus className="h-4 w-4" />
                      Build your OS with {m.short || m.name}
                      <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                    <Link
                      to="/build"
                      data-testid="module-build-cta"
                      className="hidden sm:inline-flex items-center gap-2 rounded-xl border border-border bg-card px-5 py-3.5 text-sm font-semibold transition-all hover:-translate-y-0.5 hover:bg-secondary"
                    >
                      <Layers className="h-4 w-4" />
                      Compare plans
                    </Link>
                    <button
                      type="button"
                      onClick={openDemo}
                      data-testid="module-book-demo"
                      className="inline-flex items-center gap-2 rounded-xl px-3 py-3.5 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground"
                    >
                      <CalendarCheck className="h-4 w-4" />
                      Or book a demo
                    </button>
                  </>
                )}
              </div>
              {!isBase && (
                <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    <Check className="h-3.5 w-3.5 text-emerald-500" /> Turn on / off anytime
                  </span>
                  <span className="h-1 w-1 rounded-full bg-border" />
                  <span className="inline-flex items-center gap-1.5">
                    <Check className="h-3.5 w-3.5 text-emerald-500" /> Custom-quoted after discovery
                  </span>
                  <span className="h-1 w-1 rounded-full bg-border" />
                  <span>
                    {reqBase ? <>Requires <Link to={`/products/${reqBase.id}`} className="font-semibold text-foreground/85 underline-offset-2 hover:underline">{reqBase.name}</Link></> : <>Works on Hub or Hub+</>}
                  </span>
                </div>
              )}
              {isBase && (
                <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    <Check className="h-3.5 w-3.5 text-emerald-500" /> Pricing on Build OS
                  </span>
                  <span className="h-1 w-1 rounded-full bg-border" />
                  <span className="inline-flex items-center gap-1.5">
                    <Check className="h-3.5 w-3.5 text-emerald-500" /> Cancel anytime
                  </span>
                  <span className="h-1 w-1 rounded-full bg-border" />
                  <span className="inline-flex items-center gap-1.5">
                    <Check className="h-3.5 w-3.5 text-emerald-500" /> Live in &lt; 14 days
                  </span>
                </div>
              )}
            </div>

            {/* Decorative product preview card — richer, with depth */}
            <div className="hidden lg:block">
              <div className="relative">
                {/* Outer glow */}
                <div className={`absolute -inset-10 -z-10 rounded-[2.5rem] bg-gradient-to-br ${m.color} opacity-25 blur-3xl`} />

                {/* Card */}
                <div className="relative overflow-hidden rounded-3xl border border-border bg-card shadow-2xl">
                  {/* Top brand stripe */}
                  <div className={`h-1.5 bg-gradient-to-r ${m.color}`} />

                  {/* Window chrome */}
                  <div className="flex items-center gap-2 border-b border-border/70 bg-secondary/40 px-5 py-2.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-rose-500/70" />
                    <span className="h-2.5 w-2.5 rounded-full bg-amber-500/70" />
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/70" />
                    <span className="ml-2 truncate text-[10.5px] font-medium tracking-tight text-muted-foreground">
                      clinic-os · {(m.short || m.name).toLowerCase().replace(/\s+/g, "-")}
                    </span>
                  </div>

                  {/* Body */}
                  <div className="relative p-6">
                    {/* Glow at corner */}
                    <div className={`pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-gradient-to-br ${m.color} opacity-25 blur-2xl`} />

                    <div className="relative flex items-start justify-between gap-3">
                      <span className={`inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${m.color} text-white shadow-md ring-1 ring-inset ring-white/15`}>
                        <Icon className="h-7 w-7" />
                      </span>
                      <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-emerald-600 dark:text-emerald-400">
                        <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 align-middle" />
                        Live
                      </span>
                    </div>

                    <h3 className="relative mt-5 font-display text-2xl font-black tracking-tight">{m.name}</h3>
                    <p className="relative mt-1 text-[12.5px] leading-snug text-muted-foreground">{m.tagline}</p>

                    {/* Stat strip */}
                    {isBase && (
                      <div className="relative mt-5 grid grid-cols-3 gap-2">
                        {[
                          { label: "Unlimited", value: "∞", note: "PT / Users" },
                          { label: "Native EMRs", value: "5+", note: "2-way sync" },
                          { label: "Channels", value: "5", note: "in one inbox" },
                        ].map((s) => (
                          <div key={s.label} className="rounded-xl border border-border bg-background/60 p-2.5">
                            <p className="text-[9.5px] font-bold uppercase tracking-[0.12em] text-muted-foreground">{s.label}</p>
                            <p className="mt-0.5 font-display text-lg font-black tracking-tight">{s.value}</p>
                            <p className="text-[9.5px] text-muted-foreground">{s.note}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Features list */}
                    <div className="relative mt-5 space-y-2">
                      {m.features.slice(0, 4).map((f) => (
                        <div key={f} className="flex items-start gap-2 text-[12.5px]">
                          <Check className={`mt-0.5 h-3.5 w-3.5 flex-none ${m.accent}`} strokeWidth={3} />
                          <span className="text-foreground/85">{f}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Bottom strip */}
                  <div className="border-t border-border/70 bg-secondary/40 px-5 py-2.5 text-[10.5px] font-medium text-muted-foreground">
                    {isBase ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="rounded bg-foreground/85 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-background">{m.priceLabel}/mo</span>
                        <span>per location · flat · all-in</span>
                      </span>
                    ) : (
                      <span>Snaps onto Hub or Hub+ — turn on/off anytime.</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───── DiSC PAIN-MAPPING (per-module persona alignment) ───── */}
      {(() => {
        const md = moduleDiscFor(slug);
        const copy = PAINPOINT_HEADINGS[md.disc] || PAINPOINT_HEADINGS.D;
        return (
          <div className="border-t border-border/60 pt-10 sm:pt-14">
            <PainPoints
              eyebrow={`${copy.eyebrow} for ${m.name}`}
              heading={copy.heading}
              sub="If it doesn&apos;t move these, it&apos;s a feature — not an OS module. Industry on the left. Clinic OS on the right."
              stats={STAT_SETS[md.stats] || DEFAULT_STATS}
            />
          </div>
        );
      })()}

      {/* HUB vs HUB+ COMPARISON — only on base pages */}
      {isBase && (
        <section data-testid="module-base-compare" className="border-t border-border bg-gradient-to-b from-secondary/15 to-transparent py-14 sm:py-20">
          <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
            <div className="max-w-2xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                Pick a base
              </p>
              <h2 className="mt-3 font-display text-3xl font-black tracking-tight sm:text-4xl">
                Hub or Hub+ — which is right for you?
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
                Every Clinic OS account starts with one of these two bases. Add-ons snap on top of either.
              </p>
            </div>

            <div className="mt-10 grid gap-4 lg:grid-cols-2 lg:gap-5">
              {BASES.map((b) => {
                const BIcon = b.icon;
                const isCurrent = b.id === m.id;
                const isHubPlus = b.id === "hub-plus";
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => isCurrent ? openStart(b.id) : navigate(`/products/${b.id}`)}
                    data-testid={`module-compare-${b.id}`}
                    className={`group relative overflow-hidden rounded-3xl border-2 p-6 text-left transition-all hover:-translate-y-1 hover:shadow-xl sm:p-7 ${
                      isCurrent
                        ? `${b.ring.replace("ring-", "border-")} bg-card shadow-lg ring-2 ${b.ring}`
                        : "border-border bg-card/60 hover:border-foreground/30"
                    }`}
                  >
                    {isCurrent && (
                      <span className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-foreground to-foreground/80 px-2.5 py-0.5 text-[9px] font-black uppercase tracking-[0.18em] text-background shadow-md">
                        Viewing now
                      </span>
                    )}
                    {isHubPlus && !isCurrent && (
                      <span className="absolute -top-3 right-6 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-violet-600 to-blue-600 px-2.5 py-0.5 text-[9px] font-black uppercase tracking-[0.18em] text-white shadow-md shadow-violet-600/40">
                        Most popular
                      </span>
                    )}

                    <div className="flex items-center gap-3">
                      <span className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${b.bg} ${b.accent} ring-1 ring-inset ring-white/10`}>
                        <BIcon className="h-5 w-5" />
                      </span>
                      <div>
                        <p className="font-display text-xl font-black tracking-tight">{b.name}</p>
                        <p className="text-[12px] text-muted-foreground">{b.tagline}</p>
                      </div>
                    </div>

                    <div className="mt-5 flex items-baseline gap-1.5">
                      <span className="font-display text-4xl font-black tracking-[-0.02em]">{b.priceLabel}</span>
                      <span className="text-sm text-muted-foreground">/mo</span>
                      <span className="text-[11px] text-muted-foreground">· per location</span>
                    </div>

                    <ul className="mt-5 space-y-2">
                      {b.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-[13.5px]">
                          <Check className={`mt-0.5 h-3.5 w-3.5 flex-none ${b.accent}`} strokeWidth={3} />
                          <span className="text-foreground/85">{f}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="mt-6 inline-flex items-center gap-1.5 text-[12px] font-bold">
                      {isCurrent ? (
                        <>
                          <span className={b.accent}>Start with {b.short}</span>
                          <ArrowRight className={`h-3.5 w-3.5 ${b.accent} transition-transform group-hover:translate-x-0.5`} />
                        </>
                      ) : (
                        <>
                          <span className={b.accent}>See {b.short}</span>
                          <ArrowRight className={`h-3.5 w-3.5 ${b.accent} transition-transform group-hover:translate-x-0.5`} />
                        </>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* INCLUDED ON EVERY PLAN — base pages only */}
      {isBase && (
        <section className="border-t border-border py-16 sm:py-20">
          <IncludedEverywhere />
        </section>
      )}

      {/* BENEFITS */}
      <section data-testid="module-benefits" className="border-t border-border py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <p className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] ${m.accent}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${m.bg.replace("/10", "")} bg-current`} />
              What you get
            </p>
            <h2 className="mt-4 font-display text-3xl font-black leading-[1.06] tracking-tight sm:text-4xl lg:text-5xl">
              Built to deliver{" "}
              <span className={`bg-gradient-to-r ${m.color} bg-clip-text text-transparent`}>real ROI.</span>
            </h2>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              Every line below is a system you can stop running separately — replaced by one workflow inside Clinic OS.
            </p>
          </div>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 sm:gap-5">
            {content.benefits.map((b, i) => {
              const BIcon = (b.icon && LucideIcons[b.icon]) || Check;
              return (
                <motion.div
                  key={b.title}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 transition-all hover:-translate-y-1 hover:border-foreground/25 hover:shadow-lg"
                >
                  {/* corner glow */}
                  <div className={`pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-gradient-to-br ${m.color} opacity-[0.08] blur-3xl transition-opacity group-hover:opacity-[0.18]`} />

                  <div className="relative flex items-start gap-4">
                    <span className={`inline-flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-gradient-to-br ${m.color} text-white shadow-sm ring-1 ring-inset ring-white/15`}>
                      <BIcon className="h-5 w-5" />
                    </span>
                    <div className="min-w-0">
                      <h3 className="font-display text-[17px] font-extrabold leading-tight tracking-tight sm:text-lg">{b.title}</h3>
                      <p className="mt-1.5 text-[13.5px] leading-relaxed text-muted-foreground">{b.body}</p>
                    </div>
                  </div>

                  {/* bottom accent line */}
                  <motion.div
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.6, delay: 0.15 + i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                    className={`absolute inset-x-0 bottom-0 h-[3px] origin-left bg-gradient-to-r ${m.color}`}
                  />
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* OUTCOMES — clinic-specific KPI tiles (add-ons only) */}
      {!isBase && content.outcomes && content.outcomes.length > 0 && (
        <section data-testid="module-outcomes" className="relative overflow-hidden border-t border-border py-20 sm:py-24">
          <div className={`pointer-events-none absolute -left-32 top-20 -z-0 h-72 w-72 rounded-full bg-gradient-to-br ${m.color} opacity-10 blur-3xl`} />
          <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
            <div className="max-w-2xl">
              <p className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] ${m.accent}`}>
                <span className={`h-1.5 w-1.5 rounded-full bg-gradient-to-br ${m.color}`} />
                Real outcomes
              </p>
              <h2 className="mt-4 font-display text-3xl font-black leading-[1.06] tracking-tight sm:text-4xl lg:text-5xl">
                The numbers clinics{" "}
                <span className={`bg-gradient-to-r ${m.color} bg-clip-text text-transparent`}>actually see.</span>
              </h2>
              <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-muted-foreground sm:text-base">
                Aggregated across hundreds of clinics running {m.name} alongside Hub or Hub+.
              </p>
            </div>

            <div className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
              {content.outcomes.map((o, i) => {
                const TONE = {
                  violet:  { chip: "bg-violet-500/20 text-violet-200 ring-violet-400/30", dot: "bg-violet-400", unit: "text-violet-300", bar: "from-violet-500 to-fuchsia-500", glow: "from-violet-500/30" },
                  blue:    { chip: "bg-blue-500/20 text-blue-200 ring-blue-400/30",       dot: "bg-blue-400",   unit: "text-blue-300",   bar: "from-blue-500 to-blue-400",     glow: "from-blue-500/30" },
                  emerald: { chip: "bg-emerald-500/20 text-emerald-200 ring-emerald-400/30", dot: "bg-emerald-400", unit: "text-emerald-300", bar: "from-emerald-500 to-teal-500", glow: "from-emerald-500/30" },
                  amber:   { chip: "bg-amber-500/20 text-amber-200 ring-amber-400/30",    dot: "bg-amber-400",  unit: "text-amber-300",  bar: "from-amber-500 to-orange-500",  glow: "from-amber-500/30" },
                  rose:    { chip: "bg-rose-500/20 text-rose-200 ring-rose-400/30",       dot: "bg-rose-400",   unit: "text-rose-300",   bar: "from-rose-500 to-pink-500",     glow: "from-rose-500/30" },
                }[o.accent] || { chip: "bg-violet-500/20 text-violet-200 ring-violet-400/30", dot: "bg-violet-400", unit: "text-violet-300", bar: "from-violet-500 to-fuchsia-500", glow: "from-violet-500/30" };

                return (
                  <motion.div
                    key={o.label}
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.45, delay: i * 0.06 }}
                    className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-zinc-800/80 bg-zinc-950 p-5 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.35)] ring-1 ring-inset ring-white/[0.04] transition-all hover:-translate-y-1 hover:shadow-[0_20px_50px_-12px_rgba(15,23,42,0.45)]"
                  >
                    <div className={`pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-gradient-to-br ${TONE.glow} to-transparent blur-3xl`} />
                    <div className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />

                    <span className={`relative inline-flex items-center gap-1.5 self-start rounded-full px-2 py-0.5 font-display text-[10.5px] font-black uppercase tracking-[0.18em] ring-1 ring-inset ${TONE.chip}`}>
                      <span className={`h-1 w-1 rounded-full ${TONE.dot}`} />
                      {o.label}
                    </span>

                    <p className="relative mt-4 flex items-baseline gap-1 font-display leading-none">
                      <span className="text-[2.75rem] font-black tracking-[-0.04em] text-white sm:text-[3.25rem]">{o.stat}</span>
                      {o.unit && (
                        <span className={`text-[1.25rem] font-black tracking-tight sm:text-[1.5rem] ${TONE.unit}`}>{o.unit}</span>
                      )}
                    </p>

                    <p className="relative mt-3 text-[12.5px] leading-relaxed text-white/70">
                      {o.detail}
                    </p>

                    <motion.div
                      initial={{ scaleX: 0 }}
                      whileInView={{ scaleX: 1 }}
                      viewport={{ once: true, margin: "-80px" }}
                      transition={{ duration: 0.7, delay: 0.15 + i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                      className={`absolute inset-x-0 bottom-0 h-[3px] origin-left bg-gradient-to-r ${TONE.bar}`}
                    />
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* DAY IN THE LIFE — story scenarios (add-ons only) */}
      {!isBase && content.dayInLife && content.dayInLife.length > 0 && (
        <section data-testid="module-day-in-life" className="relative overflow-hidden border-t border-border bg-secondary/30 py-20 sm:py-24">
          <div className={`pointer-events-none absolute -right-32 top-20 -z-0 h-72 w-72 rounded-full bg-gradient-to-br ${m.color} opacity-10 blur-3xl`} />
          <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
            <div className="max-w-2xl">
              <p className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] ${m.accent}`}>
                <span className={`h-1.5 w-1.5 rounded-full bg-gradient-to-br ${m.color}`} />
                A day with {m.name}
              </p>
              <h2 className="mt-4 font-display text-3xl font-black leading-[1.06] tracking-tight sm:text-4xl lg:text-5xl">
                When it{" "}
                <span className={`bg-gradient-to-r ${m.color} bg-clip-text text-transparent`}>shows up.</span>
              </h2>
              <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-muted-foreground sm:text-base">
                Concrete moments your team will feel the difference — without thinking about software.
              </p>
            </div>

            <div className="relative mt-10">
              <div className={`absolute left-5 top-0 hidden h-full w-px bg-gradient-to-b ${m.color} opacity-30 sm:block`} aria-hidden />

              <ol className="space-y-4 sm:space-y-5">
                {content.dayInLife.map((d, i) => {
                  const DIcon = (d.icon && LucideIcons[d.icon]) || Sparkles;
                  return (
                    <motion.li
                      key={d.title}
                      initial={{ opacity: 0, x: -14 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, margin: "-60px" }}
                      transition={{ duration: 0.45, delay: i * 0.07 }}
                      className="relative flex flex-col gap-3 rounded-2xl border border-border bg-card p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-foreground/25 hover:shadow-md sm:flex-row sm:items-center sm:gap-5 sm:pl-14"
                    >
                      <span className={`absolute left-5 top-1/2 hidden h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-br ${m.color} ring-4 ring-background sm:block`} aria-hidden />

                      <span className={`inline-flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-gradient-to-br ${m.color} text-white shadow-sm ring-1 ring-inset ring-white/15 sm:hidden`}>
                        <DIcon className="h-[18px] w-[18px]" />
                      </span>

                      <div className="flex w-full flex-col gap-1 sm:flex-row sm:items-center sm:gap-5">
                        <div className="flex flex-col sm:w-28 sm:flex-none">
                          <span className="font-display text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{d.time}</span>
                        </div>
                        <div className="flex flex-1 items-start gap-3 sm:items-center">
                          <span className={`hidden h-9 w-9 flex-none items-center justify-center rounded-lg bg-gradient-to-br ${m.color} text-white shadow-sm sm:inline-flex`}>
                            <DIcon className="h-[16px] w-[16px]" />
                          </span>
                          <div className="min-w-0">
                            <p className="font-display text-[15px] font-bold leading-tight tracking-tight">{d.title}</p>
                            <p className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">{d.detail}</p>
                          </div>
                        </div>
                      </div>
                    </motion.li>
                  );
                })}
              </ol>
            </div>
          </div>
        </section>
      )}

      {/* FOR WHO + REPLACES */}
      <section className="relative overflow-hidden border-t border-border bg-secondary/30 py-20 sm:py-24">
        <div className={`pointer-events-none absolute -left-32 top-10 h-72 w-72 rounded-full bg-gradient-to-br ${m.color} opacity-10 blur-3xl`} />
        <div className="mx-auto grid max-w-7xl gap-10 px-6 sm:px-8 lg:grid-cols-2 lg:gap-12 lg:px-12">
          {/* Built for */}
          <div className="relative overflow-hidden rounded-3xl border border-border bg-card p-7 shadow-sm sm:p-8">
            <div className={`pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-gradient-to-br ${m.color} opacity-[0.08] blur-3xl`} />
            <p className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.22em] text-emerald-600 dark:text-emerald-400">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Built for
            </p>
            <h2 className="mt-4 font-display text-2xl font-black leading-tight tracking-tight sm:text-3xl">
              Is this right for you?
            </h2>
            <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
              {m.name} is the right pick if you check most of these boxes.
            </p>
            <ul className="mt-6 space-y-2.5">
              {content.forWho.map((w) => (
                <li key={w} className="flex items-start gap-3 rounded-xl border border-border bg-background/60 p-3 text-[14px] transition-colors hover:border-emerald-500/30 hover:bg-emerald-500/[0.04]">
                  <span className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-md bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </span>
                  <span className="text-foreground/90">{w}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Replaces */}
          {m.replaces && m.replaces.length > 0 && (
            <div className="relative overflow-hidden rounded-3xl border border-rose-500/20 bg-card p-7 shadow-sm sm:p-8">
              <div className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-rose-500/10 blur-3xl" />
              <p className="inline-flex items-center gap-1.5 rounded-full border border-rose-500/30 bg-rose-500/10 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.22em] text-rose-600 dark:text-rose-400">
                <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
                Replaces
              </p>
              <h2 className="mt-4 font-display text-2xl font-black leading-tight tracking-tight sm:text-3xl">
                Stop paying for these.
              </h2>
              <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
                One subscription. One workflow. One vendor on the invoice.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {m.replaces.map((r) => (
                  <span
                    key={r}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-secondary/40 px-3 py-2 text-[13px] font-semibold text-foreground/75 line-through decoration-rose-400/70 decoration-2 underline-offset-4"
                  >
                    <X className="h-3.5 w-3.5 text-rose-500" strokeWidth={3} />
                    {r}
                  </span>
                ))}
              </div>

              {/* Savings strip */}
              <div className="mt-6 flex items-center gap-3 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.05] p-3.5">
                <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-sm">
                  <Check className="h-4 w-4" strokeWidth={3} />
                </span>
                <div className="min-w-0">
                  <p className="text-[12.5px] font-bold tracking-tight text-foreground">All replaced by {m.name}.</p>
                  <p className="text-[11.5px] leading-snug text-muted-foreground">One login. One billing line. One patient record.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      {content.faq && content.faq.length > 0 && (
        <section data-testid="module-faq" className="border-t border-border py-20 sm:py-24">
          <div className="mx-auto max-w-3xl px-6 sm:px-8 lg:px-12">
            <div className="text-center">
              <p className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] ${m.accent}`}>
                <span className={`h-1.5 w-1.5 rounded-full bg-gradient-to-br ${m.color}`} />
                Quick answers
              </p>
              <h2 className="mt-4 font-display text-3xl font-black leading-[1.06] tracking-tight sm:text-4xl lg:text-5xl">
                Common{" "}
                <span className={`bg-gradient-to-r ${m.color} bg-clip-text text-transparent`}>questions.</span>
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
                The stuff clinic owners ask us most often — straight answers.
              </p>
            </div>

            <div className="mt-10 space-y-3">
              {content.faq.map((item, i) => (
                <FaqItem key={item.q} item={item} index={i} accent={m.color} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RELATED — Pairs well with */}
      <section className="relative overflow-hidden border-t border-border bg-secondary/30 py-20 sm:py-24">
        <div className={`pointer-events-none absolute -right-32 top-10 h-72 w-72 rounded-full bg-gradient-to-br ${m.color} opacity-10 blur-3xl`} />
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
            <div className="max-w-xl">
              <p className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] ${m.accent}`}>
                <span className={`h-1.5 w-1.5 rounded-full bg-gradient-to-br ${m.color}`} />
                Pairs well with
              </p>
              <h2 className="mt-4 font-display text-3xl font-black leading-[1.06] tracking-tight sm:text-4xl">
                Snap these on too.
              </h2>
              <p className="mt-2 text-[14px] leading-relaxed text-muted-foreground">
                Most clinics layer these on within the first 60 days — when the use case becomes obvious.
              </p>
            </div>
            <Link
              to="/products"
              data-testid="module-related-view-all"
              className="inline-flex items-center gap-1 text-[12.5px] font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              See all add-ons <ArrowRight className="h-3 w-3" />
            </Link>
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-3 sm:gap-5">
            {related.map((r, i) => {
              const RIcon = r.icon;
              const locked = r.requiresBase && r.requiresBase !== m.id && isBase;
              return (
                <motion.div
                  key={r.id}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                >
                  <Link
                    to={`/products/${r.id}`}
                    data-testid={`module-related-${r.id}`}
                    className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-1 hover:border-foreground/25 hover:shadow-lg"
                  >
                    {/* corner glow */}
                    <div className={`pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-gradient-to-br ${r.color} opacity-[0.10] blur-3xl transition-opacity group-hover:opacity-[0.20]`} />

                    <div className="relative flex items-center justify-between">
                      <span className={`inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br ${r.color} text-white shadow-sm ring-1 ring-inset ring-white/15`}>
                        <RIcon className="h-5 w-5" />
                      </span>
                      {locked && (
                        <span className="inline-flex items-center gap-1 rounded-md bg-violet-500/15 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.12em] text-violet-600 dark:text-violet-300">
                          <Lock className="h-2.5 w-2.5" />
                          Hub+ only
                        </span>
                      )}
                    </div>

                    <h3 className="relative mt-4 font-display text-[17px] font-extrabold tracking-tight">{r.name}</h3>
                    <p className="relative mt-1.5 line-clamp-2 text-[12.5px] leading-snug text-muted-foreground">{r.tagline}</p>

                    <span className="relative mt-5 inline-flex items-center gap-1 text-[12px] font-bold text-foreground/85">
                      Learn more
                      <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                    </span>

                    {/* bottom accent bar */}
                    <div className={`absolute inset-x-0 bottom-0 h-[3px] origin-left bg-gradient-to-r ${r.color} opacity-0 transition-opacity group-hover:opacity-100`} />
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA — push to /build */}
      <section className="border-t border-border py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="relative overflow-hidden rounded-3xl border border-violet-500/30 bg-gradient-to-br from-violet-600 via-blue-600 to-violet-500 p-8 text-white shadow-xl shadow-violet-600/25 sm:p-12">
            <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-fuchsia-500/40 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -left-20 h-72 w-72 rounded-full bg-blue-400/30 blur-3xl" />
            <div className="relative max-w-2xl">
              <p className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-[10.5px] font-bold uppercase tracking-[0.22em] text-white/95 backdrop-blur">
                <Sparkles className="h-3 w-3" />
                One simple decision · 14-day go-live
              </p>
              <h2 className="mt-5 font-display text-3xl font-black leading-[1.08] tracking-tight sm:text-4xl lg:text-5xl">
                {isBase ? `Ready to start with ${m.name}?` : "Ready to add this to your clinic?"}
              </h2>
              <p className="mt-3 text-base leading-relaxed text-white/85 sm:text-lg">
                {isBase
                  ? "Pick your base, configure add-ons (or skip them), and book your walkthrough — all in one flow."
                  : "Pick a starting base on Build OS, snap this in, and book your walkthrough."}
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-2.5">
                <button
                  type="button"
                  onClick={() => openStart(isBase ? m.id : (reqBase ? reqBase.id : "hub-plus"))}
                  data-testid="module-final-cta-start"
                  className="inline-flex h-12 items-center gap-2 rounded-xl bg-white px-5 text-[13.5px] font-bold text-foreground shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg"
                >
                  <CalendarCheck className="h-3.5 w-3.5" />
                  {isBase ? `Start with ${m.short}` : "Get started"}
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
                <Link
                  to="/build"
                  data-testid="module-final-cta-build"
                  className="inline-flex h-12 items-center gap-2 rounded-xl bg-foreground/85 px-5 text-[13.5px] font-bold text-background backdrop-blur transition-all hover:-translate-y-0.5 hover:bg-foreground"
                >
                  <Layers className="h-3.5 w-3.5" />
                  See all plans on Build OS
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mount StartPlanModal */}
      {modalBase && (
        <StartPlanModal baseId={modalBase} onClose={() => setModalBase(null)} />
      )}
    </article>
  );
}

/* FAQ accordion item — animated expand on click. */
function FaqItem({ item, index, accent }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div
      data-testid={`module-faq-item-${index}`}
      className={`overflow-hidden rounded-2xl border bg-card transition-all ${
        open ? "border-foreground/25 shadow-sm" : "border-border"
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-secondary/50"
      >
        <span className="font-display text-[15px] font-bold leading-snug tracking-tight sm:text-base">
          {item.q}
        </span>
        <span className={`inline-flex h-7 w-7 flex-none items-center justify-center rounded-full bg-gradient-to-br ${accent} text-white transition-transform ${open ? "rotate-45" : ""}`}>
          <span className="text-lg font-bold leading-none">+</span>
        </span>
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <p className="px-5 pb-5 text-[14px] leading-relaxed text-muted-foreground">
            {item.a}
          </p>
        </div>
      </div>
    </div>
  );
}

